"use client"

import * as React from 'react'
import Link from 'next/link'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import type { ColumnDef } from '@tanstack/react-table'
import { Button } from '@open-mercato/ui/primitives/button'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { apiCall, apiCallOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import type { FilterDef, FilterValues } from '@open-mercato/ui/backend/FilterBar'

type CargoExtensionRow = {
  id: string
  cargoId: string
  railCargoType: string
  isoContainerSize: string | null
  intermodalSscc: string | null
  bulkMaterialCode: string | null
  wagonEvn: string | null
  createdAt: string
}

type CargoExtensionsResponse = {
  items: Array<Record<string, unknown>>
  total: number
  page: number
  totalPages: number
}

function mapCargoExtensionRow(item: Record<string, unknown>): CargoExtensionRow | null {
  const id = typeof item.id === 'string' ? item.id : null
  if (!id) return null
  return {
    id,
    cargoId: typeof item.cargoId === 'string' ? item.cargoId : (typeof item.cargo_id === 'string' ? item.cargo_id : ''),
    railCargoType: typeof item.railCargoType === 'string' ? item.railCargoType : (typeof item.rail_cargo_type === 'string' ? item.rail_cargo_type : 'GENERAL'),
    isoContainerSize: typeof item.isoContainerSize === 'string' ? item.isoContainerSize : (typeof item.iso_container_size === 'string' ? item.iso_container_size : null),
    intermodalSscc: typeof item.intermodalSscc === 'string' ? item.intermodalSscc : (typeof item.intermodal_sscc === 'string' ? item.intermodal_sscc : null),
    bulkMaterialCode: typeof item.bulkMaterialCode === 'string' ? item.bulkMaterialCode : (typeof item.bulk_material_code === 'string' ? item.bulk_material_code : null),
    wagonEvn: typeof item.wagonEvn === 'string' ? item.wagonEvn : (typeof item.wagon_evn === 'string' ? item.wagon_evn : null),
    createdAt: typeof item.createdAt === 'string' ? item.createdAt : (typeof item.created_at === 'string' ? item.created_at : ''),
  }
}

const CARGO_TYPE_VALUES = ['CONTAINER', 'INTERMODAL', 'BULK', 'GENERAL'] as const

export default function CimCargoExtensionsPage() {
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const { runMutation } = useGuardedMutation<Record<string, unknown>>({
    contextId: 'cim-cargo-extensions-list',
  })
  const [rows, setRows] = React.useState<CargoExtensionRow[]>([])
  const [page, setPage] = React.useState(1)
  const pageSize = 50
  const [total, setTotal] = React.useState(0)
  const [totalPages, setTotalPages] = React.useState(1)
  const [search, setSearch] = React.useState('')
  const [filterValues, setFilterValues] = React.useState<FilterValues>({})
  const [isLoading, setIsLoading] = React.useState(true)
  const [reloadToken, setReloadToken] = React.useState(0)
  const scopeVersion = useOrganizationScopeVersion()
  const t = useT()

  const getCargoTypeLabel = React.useCallback((railCargoType: string) => {
    const fallbackMap: Record<string, string> = {
      CONTAINER: 'Container',
      INTERMODAL: 'Intermodal',
      BULK: 'Bulk',
      GENERAL: 'General',
    }
    return t(`cim.cargoExtensions.types.${railCargoType}`, fallbackMap[railCargoType] ?? railCargoType)
  }, [t])

  const filters = React.useMemo<FilterDef[]>(() => [
    {
      id: 'railCargoType',
      label: t('cim.cargoExtensions.columns.railCargoType', 'Type'),
      type: 'select' as const,
      options: CARGO_TYPE_VALUES.map((value) => ({ value, label: getCargoTypeLabel(value) })),
    },
  ], [getCargoTypeLabel, t])

  const queryParams = React.useMemo(() => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('pageSize', String(pageSize))
    if (search.trim()) params.set('search', search.trim())
    const railCargoType = filterValues.railCargoType
    if (typeof railCargoType === 'string' && railCargoType.trim()) params.set('railCargoType', railCargoType)
    return params.toString()
  }, [filterValues, page, search])

  React.useEffect(() => {
    let cancelled = false
    async function load() {
      setIsLoading(true)
      try {
        const fallback: CargoExtensionsResponse = { items: [], total: 0, page: 1, totalPages: 1 }
        const call = await apiCall<CargoExtensionsResponse>(
          `/api/cim/cargo-extensions?${queryParams}`,
          undefined,
          { fallback },
        )
        if (!call.ok) {
          const errorPayload = call.result as { error?: string } | undefined
          const message = typeof errorPayload?.error === 'string'
            ? errorPayload.error
            : t('cim.cargoExtensions.error.load', 'Failed to load cargo extensions')
          flash(message, 'error')
          return
        }
        const payload = call.result ?? fallback
        if (!cancelled) {
          const items = Array.isArray(payload.items) ? payload.items : []
          setRows(items.map(mapCargoExtensionRow).filter((row): row is CargoExtensionRow => row !== null))
          setTotal(typeof payload.total === 'number' ? payload.total : items.length)
          setTotalPages(typeof payload.totalPages === 'number' ? payload.totalPages : 1)
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : t('cim.cargoExtensions.error.load', 'Failed to load cargo extensions')
          flash(message, 'error')
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [queryParams, reloadToken, scopeVersion, t])

  const handleRefresh = React.useCallback(() => {
    setReloadToken((token) => token + 1)
  }, [])

  const handleDelete = React.useCallback(async (ext: CargoExtensionRow) => {
    if (!ext.id) return
    const confirmed = await confirm({
      title: t('cim.cargoExtensions.deleteConfirm', 'Delete cargo extension?'),
      variant: 'destructive',
    })
    if (!confirmed) return
    try {
      await runMutation({
        operation: () => apiCallOrThrow(
          `/api/cim/cargo-extensions?id=${encodeURIComponent(ext.id)}`,
          { method: 'DELETE', headers: { 'content-type': 'application/json' } },
          { errorMessage: t('cim.cargoExtensions.error.delete', 'Failed to delete cargo extension') },
        ),
        context: {},
      })
      setRows((prev) => prev.filter((row) => row.id !== ext.id))
      setTotal((prev) => Math.max(prev - 1, 0))
      flash(t('cim.cargoExtensions.flash.deleted', 'Cargo extension deleted'), 'success')
    } catch (error) {
      const message = error instanceof Error ? error.message : t('cim.cargoExtensions.error.delete', 'Failed to delete cargo extension')
      flash(message, 'error')
    }
  }, [confirm, runMutation, t])

  const handleFiltersApply = React.useCallback((values: FilterValues) => {
    setFilterValues(values)
    setPage(1)
  }, [])

  const handleFiltersClear = React.useCallback(() => {
    setFilterValues({})
    setPage(1)
  }, [])

  const columns = React.useMemo<ColumnDef<CargoExtensionRow>[]>(() => {
    const noValue = <span className="text-muted-foreground text-sm">-</span>
    return [
      {
        accessorKey: 'cargoId',
        header: t('cim.cargoExtensions.columns.cargoId', 'Cargo ID'),
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.cargoId ? row.original.cargoId.slice(0, 8) + '…' : noValue}
          </span>
        ),
      },
      {
        accessorKey: 'railCargoType',
        header: t('cim.cargoExtensions.columns.railCargoType', 'Type'),
        cell: ({ row }) => (
          <span className="text-sm">{getCargoTypeLabel(row.original.railCargoType)}</span>
        ),
      },
      {
        accessorKey: 'isoContainerSize',
        header: t('cim.cargoExtensions.columns.isoContainerSize', 'Container Size'),
        cell: ({ row }) => row.original.isoContainerSize ?? noValue,
      },
      {
        accessorKey: 'intermodalSscc',
        header: t('cim.cargoExtensions.columns.intermodalSscc', 'SSCC'),
        cell: ({ row }) => row.original.intermodalSscc
          ? <span className="font-mono text-xs">{row.original.intermodalSscc}</span>
          : noValue,
      },
      {
        accessorKey: 'bulkMaterialCode',
        header: t('cim.cargoExtensions.columns.bulkMaterialCode', 'Material'),
        cell: ({ row }) => row.original.bulkMaterialCode ?? noValue,
      },
      {
        accessorKey: 'wagonEvn',
        header: t('cim.cargoExtensions.columns.wagonEvn', 'Wagon EVN'),
        cell: ({ row }) => row.original.wagonEvn
          ? <span className="font-mono text-xs">{row.original.wagonEvn}</span>
          : noValue,
      },
      {
        accessorKey: 'createdAt',
        header: t('cim.cargoExtensions.columns.createdAt', 'Created'),
        cell: ({ row }) => row.original.createdAt
          ? new Date(row.original.createdAt).toLocaleDateString()
          : noValue,
      },
    ]
  }, [getCargoTypeLabel, t])

  return (
    <Page>
      <PageBody>
        <DataTable<CargoExtensionRow>
          title={t('cim.cargoExtensions.title', 'Rail Cargo Extensions')}
          refreshButton={{
            label: t('cim.cargoExtensions.actions.refresh', 'Refresh'),
            onRefresh: () => { setSearch(''); setPage(1); handleRefresh() },
          }}
          actions={(
            <Button asChild>
              <Link href="/backend/cim-cargo-extensions/create">
                {t('cim.cargoExtensions.actions.create', 'New Extension')}
              </Link>
            </Button>
          )}
          columns={columns}
          data={rows}
          searchValue={search}
          onSearchChange={(value) => { setSearch(value); setPage(1) }}
          searchPlaceholder={t('cim.cargoExtensions.searchPlaceholder', 'Search by cargo ID or wagon EVN...')}
          filters={filters}
          filterValues={filterValues}
          onFiltersApply={handleFiltersApply}
          onFiltersClear={handleFiltersClear}
          perspective={{ tableId: 'cim.cargoExtensions.list' }}
          rowActions={(row) => (
            <RowActions
              items={[
                {
                  id: 'delete',
                  label: t('cim.cargoExtensions.actions.delete', 'Delete'),
                  destructive: true,
                  onSelect: () => { void handleDelete(row) },
                },
              ]}
            />
          )}
          pagination={{ page, pageSize, total, totalPages, onPageChange: setPage }}
          isLoading={isLoading}
        />
      </PageBody>
      {ConfirmDialogElement}
    </Page>
  )
}
